const inputCls =
  "w-full rounded-lg border border-slate-300 bg-[#FAF8F1] px-4 py-2.5 text-sm text-[#0B132B] placeholder-slate-400 focus:border-gold focus:bg-white focus:outline-none";

export const StepGrid = ({ children }) => (
  <div className="grid gap-5 sm:grid-cols-2">{children}</div>
);

export const StepTitle = ({ children, hint }) => (
  <div className="sm:col-span-2">
    <h3 className="text-lg font-semibold text-[#0B132B]">{children}</h3>
    {hint && <p className="mt-1 text-sm text-slate-500">{hint}</p>}
  </div>
);

export const Text = ({ label, value, onChange, required, placeholder, testid, full, type = "text" }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="mb-1.5 block text-sm font-medium text-slate-600">
      {label} {required && <span className="text-gold">*</span>}
    </label>
    <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} className={inputCls} />
  </div>
);

export const Money = ({ label, value, onChange, testid, full, optional }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="mb-1.5 block text-sm font-medium text-slate-600">
      {label} {optional && <span className="text-slate-400">(optional)</span>}
    </label>
    <div className="flex items-center rounded-lg border border-slate-300 bg-[#FAF8F1] focus-within:border-gold focus-within:bg-white">
      <span className="pl-3 text-slate-400">$</span>
      <input data-testid={testid} inputMode="numeric" value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent px-2 py-2.5 text-sm text-[#0B132B] focus:outline-none" />
    </div>
  </div>
);

export const Num = ({ label, value, onChange, testid, full, optional }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="mb-1.5 block text-sm font-medium text-slate-600">
      {label} {optional && <span className="text-slate-400">(optional)</span>}
    </label>
    <input data-testid={testid} inputMode="numeric" value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
  </div>
);

export const Select = ({ label, value, onChange, options, required, testid, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="mb-1.5 block text-sm font-medium text-slate-600">
      {label} {required && <span className="text-gold">*</span>}
    </label>
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} className={inputCls}>
      <option value="">Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export const Area = ({ label, value, onChange, placeholder, testid }) => (
  <div className="sm:col-span-2">
    <label className="mb-1.5 block text-sm font-medium text-slate-600">{label}</label>
    <textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} />
  </div>
);

export const Chips = ({ label, options, value, onChange, testid }) => (
  <div className="sm:col-span-2">
    {label && <label className="mb-2 block text-sm font-medium text-slate-600">{label}</label>}
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = value === o;
        return (
          <button key={o} type="button" onClick={() => onChange(o)}
            className={`rounded-full border px-3.5 py-1.5 text-sm transition-all ${active ? "border-gold bg-gold text-[#0B132B]" : "border-slate-300 bg-white text-slate-600 hover:border-gold/60"}`}>
            {o}
          </button>
        );
      })}
    </div>
  </div>
);

export const MultiChips = ({ label, options, values = [], onChange, testid }) => {
  const toggle = (o) => onChange(values.includes(o) ? values.filter((x) => x !== o) : [...values, o]);
  return (
    <div className="sm:col-span-2">
      {label && <label className="mb-2 block text-sm font-medium text-slate-600">{label}</label>}
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const active = values.includes(o);
          return (
            <button key={o} type="button" onClick={() => toggle(o)}
              className={`rounded-full border px-3.5 py-1.5 text-sm transition-all ${active ? "border-gold bg-gold text-[#0B132B]" : "border-slate-300 bg-white text-slate-600 hover:border-gold/60"}`}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SYS_OPTS = ["Good", "Needs Repair", "Needs Replacement", "Unknown"];

export const SystemsGrid = ({ label, items, value = {}, onChange, testid }) => (
  <div className="sm:col-span-2">
    {label && <label className="mb-3 block text-sm font-medium text-slate-600">{label}</label>}
    <div className="space-y-2" data-testid={testid}>
      {items.map((item) => (
        <div key={item} className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-[#FAF8F1] p-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm font-medium text-[#0B132B]">{item}</span>
          <div className="flex flex-wrap gap-1.5">
            {SYS_OPTS.map((o) => {
              const active = value[item] === o;
              return (
                <button key={o} type="button" onClick={() => onChange(item, o)}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-all ${active ? "border-gold bg-gold text-[#0B132B]" : "border-slate-300 bg-white text-slate-500 hover:border-gold/60"}`}>
                  {o}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const UploadStub = ({ label, onNote, testid }) => (
  <div className="sm:col-span-2">
    <label className="mb-1.5 block text-sm font-medium text-slate-600">{label}</label>
    <input type="file" multiple data-testid={testid} onChange={onNote}
      className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-full file:border-0 file:bg-gold/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-gold hover:file:bg-gold/20" />
    <p className="mt-1 text-xs text-slate-400">JPG, PNG, HEIC, PDF. Mobile camera supported. Video uploads coming soon.</p>
  </div>
);
